import {
  deleteDraftThumbnail,
  loadDraftThumbnail,
  saveDraftThumbnail,
} from "@/lib/storage/draft-files-store";
import {
  generateClipThumbnailBlob,
  generateClipThumbnailBlobFromUrl,
} from "@/lib/clip/capture-clip-frame";

const THUMBNAIL_CACHE_VERSION = 5;
const MAX_CONCURRENT = 2;

const memoryCache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

type QueueItem = {
  produce: () => Promise<Blob>;
  fileKey: string;
  storageKey: string;
  resolve: (url: string) => void;
  reject: (error: Error) => void;
};

const queue: QueueItem[] = [];
let activeCount = 0;

function thumbnailStorageKey(fileKey: string) {
  return `${fileKey}:v${THUMBNAIL_CACHE_VERSION}`;
}

function cacheThumbnailUrl(fileKey: string, blob: Blob) {
  const existing = memoryCache.get(fileKey);
  if (existing) URL.revokeObjectURL(existing);
  const url = URL.createObjectURL(blob);
  memoryCache.set(fileKey, url);
  return url;
}

export function getCachedThumbnailUrl(fileKey: string) {
  return memoryCache.get(fileKey) ?? null;
}

export function dropCachedThumbnail(fileKey: string) {
  const existing = memoryCache.get(fileKey);
  if (existing) URL.revokeObjectURL(existing);
  memoryCache.delete(fileKey);
  inflight.delete(fileKey);
}

export function clearThumbnailMemoryCache() {
  for (const url of memoryCache.values()) {
    URL.revokeObjectURL(url);
  }
  memoryCache.clear();
  inflight.clear();
  queue.length = 0;
  activeCount = 0;
}

export async function warmThumbnailCache(fileKeys: string[]) {
  await Promise.all(
    fileKeys.map(async (fileKey) => {
      if (memoryCache.has(fileKey)) return;

      const storageKey = thumbnailStorageKey(fileKey);
      const stored = await loadDraftThumbnail(storageKey);
      if (stored) cacheThumbnailUrl(fileKey, stored);
    }),
  );
}

async function generateThumbnail(
  produce: () => Promise<Blob>,
  fileKey: string,
  storageKey: string,
) {
  const blob = await produce();
  await saveDraftThumbnail(storageKey, blob);
  return cacheThumbnailUrl(fileKey, blob);
}

function drainQueue() {
  while (activeCount < MAX_CONCURRENT && queue.length > 0) {
    const item = queue.shift();
    if (!item) return;

    activeCount += 1;
    generateThumbnail(item.produce, item.fileKey, item.storageKey)
      .then(item.resolve)
      .catch((error) => {
        item.reject(
          error instanceof Error ? error : new Error("Thumbnail failed"),
        );
      })
      .finally(() => {
        activeCount -= 1;
        drainQueue();
      });
  }
}

export function requestClipThumbnail(
  fileKey: string,
  file: File | null,
  remoteUrl?: string | null,
) {
  const cached = memoryCache.get(fileKey);
  if (cached) return Promise.resolve(cached);

  const pending = inflight.get(fileKey);
  if (pending) return pending;

  const promise = (async () => {
    const storageKey = thumbnailStorageKey(fileKey);
    const stored = await loadDraftThumbnail(storageKey);
    if (stored) {
      return cacheThumbnailUrl(fileKey, stored);
    }

    const produce = file
      ? () => generateClipThumbnailBlob(file)
      : remoteUrl
        ? () => generateClipThumbnailBlobFromUrl(remoteUrl)
        : null;

    if (!produce) {
      throw new Error("Clip not ready for thumbnail generation");
    }

    return new Promise<string>((resolve, reject) => {
      queue.push({ produce, fileKey, storageKey, resolve, reject });
      drainQueue();
    });
  })().finally(() => {
    inflight.delete(fileKey);
  });

  inflight.set(fileKey, promise);
  return promise;
}

export async function removeClipThumbnail(fileKey: string) {
  dropCachedThumbnail(fileKey);
  await deleteDraftThumbnail(thumbnailStorageKey(fileKey));
}
